// src/auth.ts
// Thin fetch client for /api/auth/*.

export type { AuthorStatus } from '@ring/shared/author-trust';
import type { AuthorStatus } from '@ring/shared/author-trust';

export interface AuthorSession {
  userId:       string;
  email:        string;
  isAdmin:      boolean;
  authorStatus: AuthorStatus;
  /** Only returned by /api/auth/me (not by sign-in/register), so absent until the next session refresh. */
  storageQuotaMb?: number;
  aiEnabled?:      boolean;
  /** AI descriptions allowed in any 24 hours (shown when asking to confirm one). */
  aiImagesPerDay?: number;
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

const API = '/api/auth';

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });
  const data: Record<string, unknown> = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new ApiError(res.status, typeof data.error === 'string' ? data.error : `Request failed (${res.status})`);
  }
  return data as T;
}

export async function getSession(): Promise<AuthorSession | null> {
  const data = await apiFetch<Partial<AuthorSession>>('/me', { method: 'GET' });
  return data.email ? (data as AuthorSession) : null;
}

export function sendCode(email: string, mode: 'signin' | 'register'): Promise<void> {
  return apiFetch('/send-code', { method: 'POST', body: JSON.stringify({ email, mode }) });
}

export function verifyCode(email: string, code: string): Promise<AuthorSession> {
  return apiFetch<AuthorSession>('/verify-code', { method: 'POST', body: JSON.stringify({ email, code }) });
}

export function registerAuthor(email: string, code: string): Promise<AuthorSession> {
  return apiFetch<AuthorSession>('/register', { method: 'POST', body: JSON.stringify({ email, code }) });
}

/** Either a signed-in session, or a verified email with no account yet (confirm, then oauthRegister). */
export type OAuthSignInResult = AuthorSession | { needsRegistration: true; email: string };

export function oauthSignIn(idToken: string): Promise<OAuthSignInResult> {
  return apiFetch<OAuthSignInResult>('/oauth-signin', { method: 'POST', body: JSON.stringify({ idToken }) });
}

export function oauthRegister(idToken: string): Promise<AuthorSession> {
  return apiFetch<AuthorSession>('/oauth-register', { method: 'POST', body: JSON.stringify({ idToken }) });
}

export function signOutAuthor(): Promise<void> {
  return apiFetch('/sign-out', { method: 'POST' });
}
